import "./templates-modal.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useMemo, useState } from "react";

import type { SelectDescriptor } from "~/features/commodities/hooks/use-commodity-selection.ts";
import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { SelectCell } from "~/shared/components/trax-grid/select-cell/select-cell.tsx";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useAutoResizeColumns } from "~/shared/hooks/use-auto-resize-columns.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import type { TemplateGridItem } from "~/shared/schemas/template-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

import { useTemplateColumns } from "../../hooks/use-template-columns.tsx";
import {
	useDuplicateTemplate,
	useTemplates,
} from "../../hooks/use-templates.ts";
import { AddEditTemplateModal } from "../add-edit-template-modal/add-edit-template-modal.tsx";
import { TemplatesModalMobile } from "./templates-modal-mobile.tsx";

type TemplatesModalProps = {
	readonly customerId: number | undefined;
	readonly onClose: () => void;
	readonly onSelect: (template: TemplateGridItem) => void;
};

export const TemplatesModal = ({
	customerId,
	onClose,
	onSelect,
}: TemplatesModalProps) => {
	const isMobile = useIsMobile();
	const templatesQuery = useTemplates(customerId);
	const templates = templatesQuery.data;
	const isTemplatesLoading = templatesQuery.isLoading;

	const templatesWithId = useMemo(() => {
		return templates?.map((t) => ({
			...t,
			id: t.templateId,
		}));
	}, [templates]);

	const [select, setSelect] = useState<SelectDescriptor>({});
	const [showAddEditModal, setShowAddEditModal] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState<
		TemplateGridItem | undefined
	>(undefined);

	const toggleAddEditModal = useCallback(() => {
		setShowAddEditModal((prev) => !prev);
		if (showAddEditModal) {
			setEditingTemplate(undefined);
		}
	}, [showAddEditModal]);

	const handleEditTemplate = useCallback(
		(id: number | undefined) => {
			if (!id || !templatesWithId) return;
			const template = templatesWithId.find((t) => t.templateId === id);
			if (template) {
				setEditingTemplate(template);
				setShowAddEditModal(true);
			}
		},
		[templatesWithId],
	);

	const { duplicateTemplate, isDuplicating } = useDuplicateTemplate(templates);

	const handleCopyTemplate = useCallback(
		async (templateId: number) => {
			const newTemplateId = await duplicateTemplate(templateId);
			if (newTemplateId) {
				handleEditTemplate(newTemplateId);
			}
		},
		[duplicateTemplate, handleEditTemplate],
	);

	const handleSelect = useCallback((id: number | string) => {
		setSelect({ [id]: true });
	}, []);

	const handleDeselect = useCallback(() => {
		setSelect({});
	}, []);

	const handleApplySelection = useCallback(() => {
		const selectedEntry = Object.entries(select).find(
			([, selected]) => selected,
		);
		const selectedId = selectedEntry?.[0];

		if (selectedId && templates) {
			const template = templates.find(
				(t) => t.templateId === Number(selectedId),
			);
			if (template) {
				onSelect(template);
			}
		}
	}, [select, templates, onSelect]);

	const {
		templateColumns,
		isDeleting,
		isHandlingDefault,
		DeleteConfirmationModal,
	} = useTemplateColumns({
		customerId,
		isLoading: isTemplatesLoading || isDuplicating,
		setSelectedTemplateItem: handleEditTemplate,
		onCopyTemplate: handleCopyTemplate,
	});

	const modalColumns = useMemo(() => {
		const selectColumn: BoardColumn = {
			title: "",
			width: 50,
			show: true,
			filterable: false,
			cells: {
				data: (props: GridCustomCellProps) => (
					<SelectCell
						{...props}
						onDeselect={handleDeselect}
						onSelect={handleSelect}
						select={select}
					/>
				),
			},
		};
		return [selectColumn, ...templateColumns];
	}, [templateColumns, handleSelect, handleDeselect, select]);

	const { wrapperRef, distributedColumns } = useAutoResizeColumns(modalColumns);

	const isAnyLoading =
		isTemplatesLoading || isDeleting || isDuplicating || isHandlingDefault;

	return (
		<TraxModal
			className={"templates-modal"}
			onClose={onClose}
			title={"My Templates"}
			width={"80%"}
		>
			<div ref={wrapperRef} className={"templates-modal-grid-wrapper"}>
				{isMobile ? (
					<TemplatesModalMobile
						customerId={customerId}
						isLoading={isAnyLoading}
						onDeselect={handleDeselect}
						onEdit={handleEditTemplate}
						onSelect={handleSelect}
						select={select}
						templates={templates}
					/>
				) : (
					<TraxGrid
						className={"templates-modal-grid"}
						columns={convertToGridColumnProps(distributedColumns)}
						data={templatesWithId}
						dataItemKey={"templateId"}
						filterable={true}
						loading={isAnyLoading}
						loadingRows={10}
					/>
				)}
			</div>
			<DialogActionsBar layout={"end"}>
				<Button onClick={toggleAddEditModal} variant={"tertiary"}>
					Add New Template
				</Button>
				<Button
					disabled={!Object.values(select).some(Boolean)}
					onClick={handleApplySelection}
					variant={"primary"}
				>
					Apply Template
				</Button>
			</DialogActionsBar>
			{showAddEditModal ? (
				<AddEditTemplateModal
					onApplyTemplate={onSelect}
					template={editingTemplate}
					toggleModal={toggleAddEditModal}
				/>
			) : null}
			<DeleteConfirmationModal />
		</TraxModal>
	);
};
